import { useState, useEffect } from 'react';
import { useOutrosEventos } from '../hooks/useOutrosEventos';

/**
 * OutrosEventosOverlay — fila de notificações de gols/cartões vermelhos
 * de outros jogos em andamento no mesmo dia.
 *
 * Posicionado fixed bottom-left, acima do placar principal (z-25).
 * Uma notificação por vez; a próxima entra só após a atual sair.
 * Keyframes migrados para tailwind.config.js (animate-oev-entra).
 */
export default function OutrosEventosOverlay() {
  const { atual } = useOutrosEventos();
  const [escudoErr, setEscudoErr] = useState(false);

  useEffect(() => { setEscudoErr(false); }, [atual?.id]);

  if (!atual) return null;

  const ehGol          = atual.tipo === 'gol';
  const cor            = ehGol ? '#fbbf24' : '#f87171';
  const timeMarcou     = atual.time_evento === 'casa' ? atual.time_casa : atual.time_visitante;
  const timeAdversario = atual.time_evento === 'casa' ? atual.time_visitante : atual.time_casa;
  const escudoUrl      = atual.time_evento === 'casa' ? atual.escudo_casa : atual.escudo_visitante;

  return (
    <div
      className="fixed select-none pointer-events-none animate-oev-entra"
      style={{ bottom: 'clamp(12px, 2vw, 36px)', left: 'clamp(12px, 2vw, 36px)', zIndex: 25, width: 'clamp(220px, 22vw, 380px)' }}
    >
      <div
        style={{
          background:     ehGol
            ? 'linear-gradient(150deg, rgba(14,10,2,0.97) 0%, rgba(20,16,3,0.95) 100%)'
            : 'linear-gradient(150deg, rgba(14,3,3,0.97) 0%, rgba(20,6,6,0.95) 100%)',
          backdropFilter: 'blur(24px)',
          border:         `1.5px solid ${ehGol ? 'rgba(251,191,36,0.4)' : 'rgba(239,68,68,0.4)'}`,
          borderRadius:   'clamp(8px, 1vw, 18px)',
          boxShadow:      `0 8px 36px rgba(0,0,0,0.65), 0 0 0 1px ${ehGol ? 'rgba(251,191,36,0.08)' : 'rgba(239,68,68,0.08)'}`,
          overflow:       'hidden',
        }}
      >
        {/* ── Topo: tipo de evento + liga ── */}
        <div
          className="flex items-center justify-between px-3 py-1.5"
          style={{
            background:   `${ehGol ? 'rgba(251,191,36,0.08)' : 'rgba(239,68,68,0.08)'}`,
            borderBottom: `1px solid ${ehGol ? 'rgba(251,191,36,0.12)' : 'rgba(239,68,68,0.12)'}`,
          }}
        >
          <span style={{ fontSize: 'clamp(7px, 0.65vw, 11px)', fontWeight: 900, color: cor, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            {ehGol ? '⚽ Gol!' : '🟥 Cartão Vermelho'}
          </span>
          {atual.liga_nome && (
            <span style={{ fontSize: 'clamp(6px, 0.55vw, 10px)', color: 'rgba(255,255,255,0.3)', maxWidth: 'clamp(80px, 10vw, 160px)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {atual.liga_nome}
            </span>
          )}
        </div>

        {/* ── Corpo: escudo + time + jogador ── */}
        <div className="px-3 pt-2 pb-1.5">

          {/* Linha principal: escudo + nome do time */}
          <div className="flex items-center" style={{ gap: 'clamp(6px, 0.6vw, 12px)', marginBottom: 'clamp(2px, 0.25vw, 6px)' }}>
            {/* Escudo */}
            {escudoUrl && !escudoErr ? (
              <img
                src={escudoUrl}
                alt={timeMarcou}
                onError={() => setEscudoErr(true)}
                style={{ width: 'clamp(24px, 2.4vw, 44px)', height: 'clamp(24px, 2.4vw, 44px)', objectFit: 'contain', flexShrink: 0 }}
              />
            ) : (
              <div
                style={{
                  width: 'clamp(24px, 2.4vw, 44px)', height: 'clamp(24px, 2.4vw, 44px)',
                  borderRadius: '50%',
                  background:   `${cor}22`,
                  border:       `1px solid ${cor}40`,
                  display:      'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink:   0,
                  fontSize:     'clamp(8px, 0.7vw, 13px)',
                  fontWeight:   700, color: cor,
                  textTransform:'uppercase',
                }}
              >
                {timeMarcou.slice(0, 2)}
              </div>
            )}
            {/* Nome do time */}
            <p style={{ fontSize: 'clamp(11px, 1.05vw, 20px)', fontWeight: 900, color: 'white', lineHeight: 1.2 }}>
              {timeMarcou}
            </p>
          </div>

          {/* Jogador + minuto */}
          {atual.jogador && atual.jogador !== '?' && (
            <p style={{ fontSize: 'clamp(9px, 0.8vw, 15px)', fontWeight: 600, color: cor, lineHeight: 1.2, marginBottom: 'clamp(3px, 0.35vw, 7px)' }}>
              {atual.jogador}
              <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.35)', marginLeft: 'clamp(3px, 0.4vw, 8px)' }}>
                {atual.minuto}'
              </span>
            </p>
          )}

          {/* Separador */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', marginBottom: 'clamp(3px, 0.4vw, 8px)' }} />

          {/* Placar + times */}
          <div className="flex items-center gap-2">
            <span style={{ fontSize: 'clamp(8px, 0.75vw, 14px)', color: 'rgba(255,255,255,0.55)', fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {atual.time_casa}
            </span>
            <span
              style={{
                fontSize:     'clamp(10px, 1vw, 18px)',
                fontWeight:   900,
                color:        'white',
                background:   'rgba(255,255,255,0.1)',
                padding:      'clamp(1px, 0.1vw, 3px) clamp(4px, 0.5vw, 10px)',
                borderRadius: 'clamp(3px, 0.35vw, 7px)',
                flexShrink:   0,
                letterSpacing:'0.05em',
              }}
            >
              {atual.placar_casa} – {atual.placar_visitante}
            </span>
            <span style={{ fontSize: 'clamp(8px, 0.75vw, 14px)', color: 'rgba(255,255,255,0.55)', fontWeight: 500, flex: 1, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {atual.time_visitante}
            </span>
          </div>

          {/* Adversário */}
          <p style={{ fontSize: 'clamp(6px, 0.6vw, 11px)', color: 'rgba(255,255,255,0.25)', marginTop: 'clamp(2px, 0.2vw, 4px)', textAlign: 'center' }}>
            contra {timeAdversario}
          </p>
        </div>
      </div>
    </div>
  );
}
